import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, ShieldCheck, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { login } from '@/lib/auth';
import { useT } from '@/lib/i18n';

export function Login() {
  const navigate = useNavigate();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const decoded = await login(email, password);
      const roles = decoded.roles ?? [];
      if (roles.includes('super_admin')) {
        navigate('/superadmin/tenants', { replace: true });
      } else {
        navigate('/overview', { replace: true });
      }
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) setError(t('auth.invalidCreds'));
      else setError(t('auth.loginFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-background lg:grid lg:grid-cols-[1.05fr_1fr]">
      {/* Brand panel */}
      <aside className="relative overflow-hidden flame-gradient text-white lg:flex lg:flex-col">
        <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-white/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-16 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:24px_24px]" />

        <div className="relative flex flex-col gap-8 px-8 py-10 sm:px-12 lg:flex-1 lg:px-16 lg:py-16">
          <div className="flex flex-col gap-2">
            <img
              src="/logo-wordmark-neg.svg"
              alt="Look2it"
              className="h-10 w-auto"
            />
          </div>

          <div className="hidden lg:block lg:flex-1">
            <h1 className="max-w-md text-4xl font-bold leading-tight tracking-tight">
              {t('auth.heroTitle')}
            </h1>
            <p className="mt-4 max-w-md text-base text-white/85">
              {t('auth.heroSubtitle')}
            </p>

            <ul className="mt-10 space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <ShieldCheck className="h-4 w-4 opacity-90" strokeWidth={2.5} />
                <span className="opacity-90">{t('auth.featureSecure')}</span>
              </li>
              <li className="flex items-center gap-3">
                <WifiOff className="h-4 w-4 opacity-90" strokeWidth={2.5} />
                <span className="opacity-90">{t('auth.featureOffline')}</span>
              </li>
            </ul>
          </div>

          <div className="hidden text-xs text-white/70 lg:block">
            v{__APP_VERSION__}
          </div>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex items-center justify-center px-6 py-12 sm:px-10 lg:px-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold tracking-tight">
              {t('auth.signIn')}
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {t('auth.signInSubtitle')}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="login-email">{t('auth.email')}</Label>
              <Input
                id="login-email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="h-11"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="login-password">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="h-11 pr-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? t('auth.hidePw') : t('auth.showPw')}
                  className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {showPw ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                role="alert"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              className="h-11 w-full text-sm font-semibold glow-primary transition-shadow hover:shadow-lg"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.signingIn')}
                </>
              ) : (
                t('auth.signIn')
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            {t('auth.footerNote')}
          </p>
        </div>
      </main>
    </div>
  );
}
